import React, { useState, useEffect, useRef, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Chip,
  Badge,
  Tooltip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Drawer,
  Fade,
  LinearProgress,
  useMediaQuery
} from '@mui/material';
import { alpha, useTheme, styled } from '@mui/material/styles';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import SendIcon from '@mui/icons-material/Send';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import HandshakeIcon from '@mui/icons-material/Handshake';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';

// Enhanced Chat sub-components
import ChatScreen from '../../Components/ChatScreen';
import SendOfferDialog from '../../Pages/Dashboard/Campaigns/projectcomponents/smallercomps/sendoffer';
import PayoutDialog from '../../Pages/Dashboard/Campaigns/projectcomponents/smallercomps/payout';
import UnifiedViewOfferDialog from '../../PartnershipsDeliverablesBookings/unifiedviewoffercomponent';
import ParticipantsPanel from './chatextra';
import ConversationDetailsPanel from './conversationdetailspanel';
import AlertDialog from '../AlertDialog';
import useAlertDialog from '../useAlertDialog';

// Hooks & Utilities
import { useMutation, useQuery, useQueryClient } from 'react-query';
import client from '../../API';
import useAuth from '../../Hooks/use-auth';
import { replaceConsoleLog } from '../../Utils/toastNotifications';
import { useCreatorAuth } from '../../Hooks/creator-use-auth';
import { globalStyles } from '../../Utils/Styles';
import routes from '../../Config/routes';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../Context/SocketContext';

/* ───────────────────────── Constants ───────────────────────── */
const MOBILE_HEADER_HEIGHT = 64;
const DESKTOP_HEADER_HEIGHT = 72;
const PROGRESS_BAR_HEIGHT = 'auto';
const BOTTOM_NAV_HEIGHT = 86; // For mobile bottom nav if present

const Z_INDEX = {
  MOBILE_CHAT_OVERLAY: 1000,
  DIALOG_BACKDROP: 1300,
  DIALOG: 1400,
  MOBILE_DIALOG: 1500,
};

/* ───────────────────────── Styled Components ───────────────────────── */

const ResponsiveHeader = styled(Paper)(({ theme, ismobile }) => ({
  background: theme.palette.background.paper,
  borderRadius: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  position: 'sticky',
  top: 0,
  zIndex: 10,
  minHeight: ismobile === 'true' ? MOBILE_HEADER_HEIGHT : DESKTOP_HEADER_HEIGHT,
  maxHeight: ismobile === 'true' ? MOBILE_HEADER_HEIGHT : DESKTOP_HEADER_HEIGHT,
}));

const ModernProgressContainer = styled(Box)(({ theme, ismobile }) => ({
  background: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: theme.transitions.create(['all'], {
    duration: 300,
    easing: theme.transitions.easing.easeInOut,
  }),
  position: 'sticky',
  top: ismobile === 'true' ? MOBILE_HEADER_HEIGHT : DESKTOP_HEADER_HEIGHT,
  zIndex: 9,
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status?.toLowerCase()) {
      case 'active': return { bg: theme.palette.success.main, color: 'white' };
      case 'pending': return { bg: theme.palette.warning.main, color: 'white' };
      case 'completed': return { bg: theme.palette.info.main, color: 'white' };
      case 'declined': return { bg: theme.palette.error.main, color: 'white' };
      default: return { bg: theme.palette.grey[400], color: 'white' };
    }
  };

  const colors = getStatusColor();
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 600,
    fontSize: '0.68rem',
    height: 22,
    borderRadius: 11,
    transition: theme.transitions.create(['transform', 'box-shadow'], {
      duration: 200,
    }),
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows[4],
    },
  };
});

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: '100vw',
    height: '100vh',
    padding: 0,
    borderRadius: 0,
    backgroundColor: theme.palette.background.default,
  },
}));

const DesktopDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 380,
    padding: theme.spacing(2),
    borderRadius: `${theme.spacing(2)} 0 0 ${theme.spacing(2)}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[8],
  },
}));

/* ───────────────────────── Partnership Progress Calculation ───────────────────────── */

const calculatePartnershipProgress = (partnership, deliverables = []) => {
  const steps = [
    {
      id: 'created',
      label: 'Created',
      description: 'Partnership initiated',
      completed: true,
      required: true,
    },
    {
      id: 'negotiation',
      label: 'Negotiation',
      description: 'Terms being discussed',
      completed: partnership?.status?.toLowerCase() !== 'pending',
      required: true,
    },
    {
      id: 'active',
      label: 'Active',
      description: 'Partnership is active',
      completed: ['active', 'completed'].includes(partnership?.status?.toLowerCase()),
      required: true,
    },
    {
      id: 'deliverables',
      label: 'Deliverables',
      description: 'Work completed',
      completed: deliverables.every(d => ['completed', 'accept', 'accepted'].includes(d.status?.toLowerCase())),
      required: deliverables.length > 0,
    },
    {
      id: 'payment',
      label: 'Payment',
      description: 'Payment completed',
      completed: partnership?.payment_status === 'completed',
      required: true,
    },
  ];

  const requiredSteps = steps.filter(step => step.required);
  const completedSteps = requiredSteps.filter(step => step.completed);
  const currentStep = requiredSteps.findIndex(step => !step.completed);

  const progressPercentage = requiredSteps.length > 0 ? (completedSteps.length / requiredSteps.length) * 100 : 0;

  const status = progressPercentage === 100
    ? 'completed'
    : progressPercentage > 0
    ? 'in-progress'
    : 'not-started';

  return {
    steps: requiredSteps,
    completedCount: completedSteps.length,
    totalCount: requiredSteps.length,
    progressPercentage,
    currentStepIndex: currentStep === -1 ? requiredSteps.length - 1 : currentStep,
    status,
    nextAction: currentStep === -1 ? null : requiredSteps[currentStep].label
  };
};

const formatWorkflowStage = (stage) => {
  if (!stage) return null;
  return stage
    .toString()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/* ───────────────────────── Enhanced Progress Bar ───────────────────────── */

const ResponsiveProgressBar = ({ 
  partnership, 
  deliverables = [],
  onGuidanceOpen,
  isMobile
}) => {
  const theme = useTheme();
  
  const progress = useMemo(() => 
    calculatePartnershipProgress(partnership, deliverables), 
    [partnership, deliverables]
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return theme.palette.success.main;
      case 'in-progress': return theme.palette.primary.main;
      default: return theme.palette.grey[400];
    }
  };

  const pendingDeliverables = deliverables.filter(d => 
    ['pending', 'negotiate'].includes(d.status?.toLowerCase())
  ).length;
  const workflowStageLabel = formatWorkflowStage(partnership?.workflow_stage) || progress.nextAction || 'Complete';

  return (
    <ModernProgressContainer ismobile={isMobile.toString()}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between', 
        flexWrap: 'wrap',
        gap: { xs: 1, sm: 1.25 },
        px: { xs: 2, sm: 3 },
        py: { xs: 1.25, sm: 1.5 },
        minHeight: { xs: 52, sm: 58 },
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 1.5 }, minWidth: 0, flex: 1 }}>
          <Avatar sx={{ 
            bgcolor: theme.palette.primary.main, 
            width: { xs: 30, sm: 36 }, 
            height: { xs: 30, sm: 36 },
            boxShadow: theme.shadows[2],
          }}>
            <HandshakeIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
          </Avatar>
          
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progress.progressPercentage}
              sx={{
                height: { xs: 5, sm: 6 },
                borderRadius: { xs: 3, sm: 4 },
                backgroundColor: alpha(theme.palette.grey[300], 0.3),
                mb: 0.5,
                '& .MuiLinearProgress-bar': {
                  background: `linear-gradient(90deg, ${getStatusColor(progress.status)} 0%, ${alpha(getStatusColor(progress.status), 0.8)} 100%)`,
                  borderRadius: { xs: 3, sm: 4 },
                  boxShadow: `0 2px 8px ${alpha(getStatusColor(progress.status), 0.3)}`,
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ fontWeight: 500 }}>
                {progress.completedCount}/{progress.totalCount} steps
              </Typography>
              <Typography
                variant={isMobile ? "caption" : "body2"}
                color="text.secondary"
                sx={{ fontWeight: 600, ml: 1, textAlign: 'right' }}
              >
                {workflowStageLabel}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.75 }, ml: 'auto', flexShrink: 0 }}>
          <StatusChip 
            size="small"
            label={partnership?.status || 'Unknown'}
            status={partnership?.status}
          />
          {pendingDeliverables > 0 && (
            <Badge badgeContent={pendingDeliverables} color="error">
              <AssignmentIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: 'warning.main' }} />
            </Badge>
          )}

          <Tooltip title="Partnership guidance">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onGuidanceOpen();
              }}
              sx={{ 
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                width: { xs: 32, sm: 36 },
                height: { xs: 32, sm: 36 },
                '&:hover': { 
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                  transform: 'translateY(-1px)',
                  boxShadow: theme.shadows[4],
                },
                transition: 'all 0.2s ease',
              }}
            >
              <LightbulbIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: 'primary.main' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </ModernProgressContainer>
  );
};

/* ───────────────────────── Next Steps Guidance System ───────────────────────── */

const getNextStepsForPartnership = (isCreator, partnership, deliverables) => {
  const partnershipStatus = partnership?.status?.toLowerCase();
  const pendingDeliverables = deliverables.filter(d => 
    ['pending', 'negotiate'].includes(d.status?.toLowerCase())
  );
  const hasActiveDeliverables = deliverables.some(d => 
    ['accept', 'accepted', 'active'].includes(d.status?.toLowerCase())
  );
  const allDeliverablesComplete = deliverables.length > 0 && 
    deliverables.every(d => ['completed', 'paid'].includes(d.status?.toLowerCase()));

  if (isCreator) {
    if (partnershipStatus === 'pending') {
      return {
        title: "Partnership Under Review",
        subtitle: "Waiting for brand approval",
        priority: 'low',
        steps: [
          {
            title: "Partnership Submitted",
            description: "Your partnership proposal has been submitted to the brand",
            icon: <CheckCircleIcon />,
            completed: true
          },
          {
            title: "Awaiting Brand Response",
            description: "The brand is reviewing your proposal and will respond soon",
            icon: <PendingIcon />,
            completed: false
          }
        ]
      };
    }

    if (pendingDeliverables.length > 0) {
      return {
        title: "Review Pending Offers",
        subtitle: `${pendingDeliverables.length} offers need your response`,
        priority: 'high',
        steps: [
          {
            title: "Review New Offers",
            description: "Check the offers sent by the brand and decide whether to accept, decline, or negotiate",
            icon: <LocalOfferIcon />,
            completed: false
          },
          {
            title: "Respond to Each Offer",
            description: "Accept offers you're happy with, or propose counter-offers for negotiation",
            icon: <CheckCircleIcon />,
            completed: false
          }
        ]
      };
    }

    if (hasActiveDeliverables && !allDeliverablesComplete) {
      return {
        title: "Complete Your Work",
        subtitle: "Work on accepted deliverables",
        priority: 'medium',
        steps: [
          {
            title: "Complete Deliverables",
            description: "Finish the work outlined in your accepted offers",
            icon: <AssignmentIcon />,
            completed: false
          },
          {
            title: "Submit Your Work",
            description: "Upload completed work and notify the brand",
            icon: <SendIcon />,
            completed: false
          }
        ]
      };
    }

    return {
      title: "Partnership Active",
      subtitle: "Stay connected with the brand",
      priority: 'low',
      steps: [
        {
          title: "Monitor Partnership",
          description: "Stay active in the chat and respond to any brand communications",
          icon: <NotificationsActiveIcon />,
          completed: false
        }
      ]
    };

  } else {
    if (partnershipStatus === 'pending') {
      return {
        title: "Partnership Pending",
        subtitle: "Review and respond to partnership request",
        priority: 'high',
        steps: [
          {
            title: "Review Partnership Proposal",
            description: "Examine the creator's partnership proposal and their profile",
            icon: <AssignmentIcon />,
            completed: false
          },
          {
            title: "Accept or Decline Partnership",
            description: "Decide whether to move forward with this creator partnership",
            icon: <CheckCircleIcon />,
            completed: false
          }
        ]
      };
    }

    if (pendingDeliverables.length > 0) {
      return {
        title: "Creator Responses Pending",
        subtitle: `Waiting for creator to respond to ${pendingDeliverables.length} offers`,
        priority: 'medium',
        steps: [
          {
            title: "Offers Sent",
            description: "Your offers have been sent to the creator for review",
            icon: <CheckCircleIcon />,
            completed: true
          },
          {
            title: "Waiting for Creator Response",
            description: "The creator will accept, decline, or negotiate your offers",
            icon: <PendingIcon />,
            completed: false
          }
        ]
      };
    }

    return {
      title: "Manage Partnership",
      subtitle: "Continue overseeing partnership progress",
      priority: 'medium',
      steps: [
        {
          title: "Create Offers",
          description: "Send deliverable offers to keep the partnership active",
          icon: <LocalOfferIcon />,
          completed: false
        },
        {
          title: "Maintain Communication",
          description: "Stay in touch with the creator about partnership progress",
          icon: <NotificationsActiveIcon />,
          completed: false
        }
      ]
    };
  }
};

/* ───────────────────────── Partnership Guidance Dialog ───────────────────────── */

const PartnershipGuidanceDialog = ({ 
  open, 
  onClose, 
  isCreator,
  partnership,
  deliverables,
  onSendOffer,
  isMobile
}) => {
  const theme = useTheme();
  const nextSteps = getNextStepsForPartnership(isCreator, partnership, deliverables);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return theme.palette.error.main;
      case 'medium': return theme.palette.warning.main;
      case 'low': return theme.palette.success.main;
      default: return theme.palette.primary.main;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={Fade}
      sx={{ zIndex: isMobile ? Z_INDEX.MOBILE_DIALOG : Z_INDEX.DIALOG }}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '16px',
          maxWidth: isMobile ? '100vw' : '500px',
          height: isMobile ? '100vh' : 'auto',
          maxHeight: isMobile ? '100vh' : '90vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        pb: 1,
        px: { xs: 2, sm: 3 },
        pt: { xs: 2, sm: 3 },
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: getPriorityColor(nextSteps.priority) }}>
            <LightbulbIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Partnership Guide
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {nextSteps.title}
            </Typography>
          </Box>
        </Box>
        {isMobile && (
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
          {nextSteps.subtitle}
        </Typography>

        <List sx={{ py: 0 }}>
          {nextSteps.steps.map((step, index) => (
            <ListItem key={index} sx={{ px: 0, py: { xs: 0.5, sm: 1 } }}>
              <ListItemIcon>
                <Avatar 
                  sx={{ 
                    bgcolor: step.completed ? theme.palette.success.main : alpha(getPriorityColor(nextSteps.priority), 0.1),
                    color: step.completed ? 'white' : getPriorityColor(nextSteps.priority),
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 }
                  }}
                >
                  {step.completed ? <CheckCircleIcon /> : step.icon}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600,
                      textDecoration: step.completed ? 'line-through' : 'none',
                      opacity: step.completed ? 0.7 : 1,
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }}
                  >
                    {step.title}
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {step.description}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions sx={{ 
        p: { xs: 2, sm: 3 }, 
        gap: 1,
        flexDirection: { xs: 'column', sm: 'row' },
      }}>
        <Button 
          onClick={onClose} 
          variant="outlined" 
          fullWidth={isMobile}
          sx={{ order: { xs: 3, sm: 1 } }}
        >
          Got it!
        </Button>
        {!isCreator && onSendOffer && (
          <Button 
            onClick={onSendOffer} 
            variant="contained" 
            startIcon={<LocalOfferIcon />} 
            color="secondary"
            fullWidth={isMobile}
            sx={{ order: { xs: 1, sm: 2 } }}
          >
            Send Offer
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

/* ───────────────────────── Main Partnership Chat Screen Component ───────────────────────── */

const PartnershipChatScreen = forwardRef(function PartnershipChatScreen({
  partnershipId,
  onNavigateBack,
  sx = {},
  children
}, ref) {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getCurrrentUser } = useAuth();
  const { creatorToken } = useCreatorAuth();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  
  // Enhanced device detection
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isMobileSafari = isMobile && isSafari;

  /* ------------------------------------------------------------------ */
  /*  State Management                                                  */
  /* ------------------------------------------------------------------ */
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [sendOfferOpen, setSendOfferOpen] = useState(false);
  const [sendPayoutOpen, setSendPayoutOpen] = useState(false);
  const [viewOfferOpen, setViewOfferOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [guidanceDialogOpen, setGuidanceDialogOpen] = useState(false);

  /* ------------------------------------------------------------------ */
  /*  WebSocket Integration                                             */
  /* ------------------------------------------------------------------ */
  useSocket();

  /* ------------------------------------------------------------------ */
  /*  Derived Variables                                                 */
  /* ------------------------------------------------------------------ */
  const userInfo = getCurrrentUser();
  const isUser = !!userInfo && !creatorToken;
  const isCreator = !!creatorToken && !userInfo;

  /* ------------------------------------------------------------------ */
  /*  iOS Safari Viewport Fix                                          */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (isMobileSafari) {
      const setVH = () => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      };
      
      setVH();
      window.addEventListener('resize', setVH);
      window.addEventListener('orientationchange', setVH);
      
      return () => {
        window.removeEventListener('resize', setVH);
        window.removeEventListener('orientationchange', setVH);
      };
    }
  }, [isMobileSafari]);

  /* ------------------------------------------------------------------ */
  /*  Data Fetching                                                     */
  /* ------------------------------------------------------------------ */
  const {
    data: partnershipFetch,
    isLoading: loadingPartnership,
    error: partnershipError,
    refetch: partnershipRefetch,
  } = useQuery(
    ["partnerships", partnershipId],
    () => {
      if (isCreator) {
        return client.creatorConnect.getPartnershipSingle(
          partnershipId,
          creatorToken.creator_user.username
        );
      } else {
        return client.partnerships.fetchPartnership(partnershipId);
      }
    },
    { 
      enabled: !!partnershipId,
      refetchOnWindowFocus: false,
    }
  );

  const partnership = isCreator ? 
    partnershipFetch?.partnership : 
    partnershipFetch?.partnership;

  const conversationId = partnership?.conversation_id || null;

  /* ------------------------------------------------------------------ */
  /*  Conversation bootstrap                                            */
  /* ------------------------------------------------------------------ */
  const createConversation = useMutation(
    () => client.conversations.createPartnership({ partnership_id: partnershipId }),
    { onSuccess: () => partnershipRefetch() }
  );

  useEffect(() => {
    if (partnershipFetch && !conversationId) {
      createConversation.mutate();
    }
  }, [partnershipFetch, conversationId]);

  /* ------------------------------------------------------------------ */
  /*  Messages Query                                                    */
  /* ------------------------------------------------------------------ */
  const {
    refetch: refetchMessages,
    data: messagesData
  } = useQuery(
    ["messages", conversationId],
    () => client.conversations.getMessages(conversationId),
    {
      enabled: !!conversationId,
      refetchOnWindowFocus: false,
    }
  );

  /* ------------------------------------------------------------------ */
  /*  Deliverables Calculation                                          */
  /* ------------------------------------------------------------------ */
  const deliverables = useMemo(() => {
    if (!messagesData?.messages) return [];
    
    return messagesData.messages
      .filter(msg => msg.attachments?.attachment_type === 'deliverable' || msg.deliverable)
      .map(msg => msg.attachments?.data || msg.deliverable)
      .filter(Boolean);
  }, [messagesData]);

  const handleGuidanceOpen = useCallback(() => {
    setGuidanceDialogOpen(true);
  }, []);

  const handleViewOffer = useCallback((deliverable) => {
    setSelectedOffer(deliverable);
    setViewOfferOpen(true);
  }, []);

  const handleUpdateDeliverable = useCallback((deliverableId, update) => {
    replaceConsoleLog('Updating deliverable', { deliverableId, update });
    refetchMessages();
  }, [refetchMessages]);

  const handleSendStatusMessage = useCallback((statusMessage) => {
    if (!conversationId) return;
    replaceConsoleLog('Sending status message', statusMessage);
  }, [conversationId]);

  const handleToggleDetails = useCallback(() => {
    setDetailsOpen((prev) => !prev);
  }, []);

  const handleToggleParticipants = useCallback(() => {
    setParticipantsOpen((prev) => !prev);
  }, []);

  /* ------------------------------------------------------------------ */
  /*  Dialog Z-Index Helper                                             */
  /* ------------------------------------------------------------------ */
  const getDialogZIndex = useCallback(() => {
    return isMobile ? Z_INDEX.MOBILE_DIALOG : Z_INDEX.DIALOG;
  }, [isMobile]);

  /* ------------------------------------------------------------------ */
  /*  Navigation                                                        */
  /* ------------------------------------------------------------------ */
  const handleNavigateBack = () => {
    if (onNavigateBack) {
      onNavigateBack();
    } else if (isCreator) {
      navigate(routes.creatorCollabs);
    } else {
      navigate("/collabs");
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Loading and Error States                                          */
  /* ------------------------------------------------------------------ */
  if (loadingPartnership) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: isMobile ? `calc(100vh - ${BOTTOM_NAV_HEIGHT}px)` : "100vh",
          backgroundColor: "background.default",
        }}
      >
        <CircularProgress size={isMobile ? 40 : 60} />
      </Box>
    );
  }

  if (partnershipError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: isMobile ? `calc(100vh - ${BOTTOM_NAV_HEIGHT}px)` : "100vh",
          backgroundColor: "background.default",
          p: 3,
        }}
      >
        <Typography color="error" variant="h6" gutterBottom>
          Error fetching partnership
        </Typography>
        <Typography color="text.secondary" variant="body2" textAlign="center">
          {partnershipError.message}
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()} 
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if (!partnership) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: isMobile ? `calc(100vh - ${BOTTOM_NAV_HEIGHT}px)` : "100vh",
          backgroundColor: "background.default",
          p: 3,
        }}
      >
        <Typography variant="h6" gutterBottom>
          No partnership found
        </Typography>
        <Button variant="outlined" onClick={handleNavigateBack} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Render                                                            */
  /* ------------------------------------------------------------------ */
  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          ...globalStyles.heightMinusToolbar,
          height: isMobile ? `calc(100vh - ${BOTTOM_NAV_HEIGHT}px)` : `calc(100vh - 1px)`,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
          position: 'relative',
          backgroundColor: "background.default",
          ...(isIOS && {
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }),
          ...sx
        }}
      >
        <AlertDialog alertState={dialogState} />

        {/* Responsive Header */}
        <ResponsiveHeader ismobile={isMobile.toString()}>
          <Box sx={{
            px: { xs: 1.5, sm: 2.25 },
            py: { xs: 0.75, sm: 1 },
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.5 },
            minHeight: { xs: 56, sm: 64 },
          }}>
            <IconButton
              onClick={handleNavigateBack}
              size="small"
              sx={{ 
                p: 0.5,
                bgcolor: 'transparent',
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: alpha(theme.palette.action.hover, 0.2),
                  transform: 'translateX(-2px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>

            <Avatar 
              src={partnership.creator_pfphref || partnership.pfphref} 
              alt={partnership.creator || "Creator"}
              sx={{ 
                width: { xs: 34, sm: 40 }, 
                height: { xs: 34, sm: 40 },
                border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
              }}
            />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                variant={isMobile ? "subtitle1" : "h6"} 
                noWrap 
                sx={{ fontWeight: 700, fontSize: { xs: '0.98rem', sm: '1.1rem' }, lineHeight: 1.2 }}
              >
                @{partnership.creator || "Creator"}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                noWrap
                sx={{ fontSize: { xs: '0.76rem', sm: '0.82rem' } }}
              >
                {partnership.name || "Partnership Chat"}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
              <StatusChip 
                size="small"
                label={partnership?.status || 'Unknown'}
                status={partnership?.status}
              />
              <Tooltip title="Partnership guidance">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGuidanceOpen();
                  }}
                  sx={{ 
                    width: 32,
                    height: 32,
                    bgcolor: alpha(theme.palette.action.hover, 0.1),
                    color: 'text.secondary',
                    '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.2) },
                  }}
                >
                  <LightbulbIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {conversationId && (
                <Tooltip title="Add participants">
                  <IconButton 
                    onClick={handleToggleParticipants} 
                    size="small"
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: alpha(theme.palette.action.hover, 0.1),
                      '&:hover': {
                        bgcolor: alpha(theme.palette.action.hover, 0.2),
                      },
                    }}
                  >
                    <PersonAddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              
              <Tooltip title="Conversation details">
                <IconButton 
                  onClick={handleToggleDetails} 
                  size="small"
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: alpha(theme.palette.action.hover, 0.1),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.action.hover, 0.2),
                    },
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </ResponsiveHeader>

        {/* Chat Screen */}
        <ChatScreen
          ref={ref}
          conversationId={conversationId}
          partnershipId={partnershipId}
          creatorId={partnership?.creator}
          creator={{
            creator: partnership?.creator,
            display_name: partnership?.creator,
            pfphref: partnership?.creator_pfphref || partnership?.pfphref
          }}
          onDeliverableClick={handleViewOffer}
          onDeliverablesUpdate={(updatedDeliverables) => {
            replaceConsoleLog('Deliverables updated', updatedDeliverables);
          }}
          showWarning={true}
          chatSpacing={0.5}
          margin={isMobile ? 0.75 : 1}
          conversationTheme={{
            ...(partnership?.theme || {}),
            layout: 'partnership-clean',
            color: '#27405f',
          }}
          sx={{ 
            flex: 1, 
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            pb: 0,
            position: 'relative',
            overflow: 'hidden',
            WebkitOverflowScrolling: 'touch',
          }}
        />

        {/* Participants Panel Dialog */}
        <Dialog
          open={participantsOpen}
          onClose={handleToggleParticipants}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          sx={{ zIndex: getDialogZIndex() }}
          PaperProps={{
            sx: {
              borderRadius: { xs: 0, sm: 3 },
              height: { xs: '85vh', sm: '80vh' },
              maxHeight: { xs: '85vh', sm: '80vh' },
            }
          }}
        >
          <ParticipantsPanel
            conversationId={conversationId}
            onClose={handleToggleParticipants}
          />
        </Dialog>

        {/* Conversation Details Panel Dialog */}
        <Dialog
          open={detailsOpen}
          onClose={handleToggleDetails}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          sx={{ zIndex: getDialogZIndex() }}
          PaperProps={{
            sx: {
              borderRadius: { xs: 0, sm: 3 },
              height: { xs: '85vh', sm: '80vh' },
              maxHeight: { xs: '85vh', sm: '80vh' },
            }
          }}
        >
          <ConversationDetailsPanel
            conversationId={conversationId}
            onClose={handleToggleDetails}
            // Pass partnership-specific handlers as props to be used by the details panel
            onSendOffer={!isCreator ? () => {
              setDetailsOpen(false);
              setSendOfferOpen(true);
            } : undefined}
            onSendPayout={!isCreator ? () => {
              setDetailsOpen(false);
              setSendPayoutOpen(true);
            } : undefined}
            partnershipId={partnershipId}
            partnership={partnership}
            deliverables={deliverables}
            onViewDeliverable={handleViewOffer}
          />
        </Dialog>

        {/* Send Offer Dialog */}
        {!isCreator && (
          <SendOfferDialog
            open={sendOfferOpen}
            onClose={() => setSendOfferOpen(false)}
            partnershipId={partnershipId}
            creatorId={partnership.creator}
            conversationId={conversationId}
            sx={{ zIndex: getDialogZIndex() }}
          />
        )}

        {/* Payout Dialog */}
        {!isCreator && (
          <PayoutDialog
            open={sendPayoutOpen}
            onClose={() => setSendPayoutOpen(false)}
            partnershipId={partnershipId}
            creatorId={partnership.creator}
            conversationId={conversationId}
            sx={{ zIndex: getDialogZIndex() }}
          />
        )}

        {/* View Offer Dialog */}
        {selectedOffer && (
          <UnifiedViewOfferDialog
            open={viewOfferOpen}
            onClose={() => {
              setViewOfferOpen(false);
              setSelectedOffer(null);
            }}
            deliverable={selectedOffer}
            conversationId={conversationId}
            onUpdateDeliverable={handleUpdateDeliverable}
            onSendMessage={handleSendStatusMessage}
            onOfferUpdated={(updatedData) => {
              replaceConsoleLog('Offer updated', updatedData);
              refetchMessages();
            }}
            isCreator={isCreator}
            userInfo={userInfo}
            creatorToken={creatorToken}
            sx={{ zIndex: getDialogZIndex() }}
          />
        )}

        {/* Partnership Guidance Dialog */}
        <PartnershipGuidanceDialog
          open={guidanceDialogOpen}
          onClose={() => setGuidanceDialogOpen(false)}
          isCreator={isCreator}
          partnership={partnership}
          deliverables={deliverables}
          isMobile={isMobile}
          onSendOffer={() => {
            setGuidanceDialogOpen(false);
            setSendOfferOpen(true);
          }}
        />

        {children}
      </Box>
    </Fade>
  );
});

export default PartnershipChatScreen;
